<script>
import StaticTextDisplay from '../../displayComponents/StaticText.vue';
import StaticImageDisplay from '../../displayComponents/StaticImage.vue';
import metricComponentsMixin from '../../mixins/metricComponentsMixin.js';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    StaticTextDisplay,
    StaticImageDisplay,
  },
  mixins: [metricComponentsMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    display: {
      type: Object,
      required: true,
    },
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    widgetVisualizationOptions: {
      type: Object,
      default: () => ({}),
    },
    staticTextContent: {
      type: String,
      default: () => '',
    },
    imageLinkContent: {
      type: String,
      default: () => '',
    },
  },
  data() {
    return {
      editingWidget: false, // Indica se o widget está em modo de edição
      isLoading: true, // Estado de carregamento
      noTextToDisplay: false, // Flag para determinar se há ou não texto para exibir
      noImageToDisplay: false, // Flag para determinar se há ou não imagem para exibir
      summaryData: {}, // Dados de resumo carregados
      shouldShowPreview: false, // Indica se a pré-visualização deve ser exibida
      componentWidth: 0, // Largura atual do componente
    };
  },

  computed: {
    // Verifica se o usuário está na página de edição ou criação do widget
    canEdit() {
      const onEditPage = this.$route.path.endsWith('/edit');
      const onCreationPage =
        this.$route.path.endsWith('/custom_dashboards') &&
        !this.$route.params?.dashboardId;
      return onEditPage || onCreationPage;
    },
    // Conteúdo a ser exibido no resumo
    summaryContent() {
      return this.summaryData;
    },
    // Define a altura do conteúdo do widget dinamicamente
    widgetContentHeightClass() {
      const { metric, dateRange, inbox } = this.widgetVisualizationOptions;
      const showingDescriptions = Boolean(
        metric || dateRange || (inbox && this.inbox?.id)
      );
      return showingDescriptions ? 'h-[calc(100%-40px)]' : 'h-full';
    },
    shouldShowNoTextAlert() {
      return this.noTextToDisplay && this.isStaticTextDisplay;
    },
    shouldShowNoImageAlert() {
      return this.noImageToDisplay && this.isStaticImageDisplay;
    },
  },

  methods: {
    // Define a data inicial com base no intervalo de datas selecionado
    setInitialData() {
      this.summaryData = '';
    },

    // Abre o modal de edição do widget
    openEditModal() {
      emitter.emit('openEditWidgetModal', { id: this.id });
    },

    fetchData() {
      if (this.isStaticTextDisplay) {
        // Busca o texto para exibir
        this.summaryData = this.staticTextContent || '';
        if (this.summaryData === '') this.noTextToDisplay = true;
        this.isLoading = false;
      } else if (this.isStaticImageDisplay) {
        // Busca a imagem para exibir
        this.summaryData = this.imageLinkContent || '';
        if (this.summaryData === '') this.noImageToDisplay = true;
        this.isLoading = false;
      }
    },

    // Exibe a pré-visualização do widget
    showPreview() {
      this.shouldShowPreview = true;
      this.fetchData();
    },
  },
};
</script>

<template>
  <div
    ref="widget"
    class="max-h-full h-full overflow-auto pt-3 px-3 mx-4 widget-container"
  >
    <!-- Container principal do widget -->
    <div class="w-full flex relative">
      <div class="flex gap-1 max-w-[calc(100%-40px)] flex-wrap">
        <!-- NOME DA MÉTRICA SELECIONADA -->
        <div
          v-if="widgetVisualizationOptions.metric"
          class="widget_preferences_tags bg-slate-50 border-slate-50"
        >
          {{ $t('CUSTOM_DASHBOARDS.WIDGET_TYPES.STATIC') }}
        </div>
      </div>

      <!-- SEÇÃO DE BOTÕES DE EDIÇÃO/GRAVAÇÃO DO WIDGET -->
      <div v-if="canEdit" class="z-[999] absolute right-0">
        <!-- BOTÃO PARA ABRIR A SEÇÃO DE EDIÇÃO -->
        <woot-button
          class="flex items-center"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="settings"
          @click="openEditModal"
        />
      </div>
    </div>

    <!-- Conteúdo principal do widget -->
    <div
      class="w-full pb-3 flex justify-center items-center"
      :class="widgetContentHeightClass"
    >
      <!-- Adicionando botão de prévia para evitar requisições desnecessárias.
      A requisição é feita apenas quando o cliente exibe o display (não está editando/criando).
      Isso resolve o problema de múltiplas requisições desnecessárias ao modificar um único widget -->
      <woot-button v-if="canEdit && !shouldShowPreview" @click="showPreview">
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.VIEW_PREVIEW') }}
      </woot-button>
      <div v-else class="w-full h-full">
        <!-- SPINNER AGUARDANDO O FIM DA REQUISIÇÃO -->
        <woot-loading-state
          v-if="isLoading"
          class="text-xxs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <!-- QUANDO NÃO TIVER TEXTO PARA SER EXIBIDO -->
        <div
          v-else-if="shouldShowNoTextAlert"
          class="text-xs w-full h-full flex justify-center items-center"
        >
          {{ $t('CUSTOM_DASHBOARDS.STATIC_TEXT.NO_TEXT') }}
        </div>
        <!-- QUANDO NÃO TIVER IMAGEM PARA SER EXIBIDA -->
        <div
          v-else-if="shouldShowNoImageAlert"
          class="text-xs w-full h-full flex justify-center items-center"
        >
          {{ $t('CUSTOM_DASHBOARDS.STATIC_IMAGE.NO_IMAGE') }}
        </div>
        <!-- DADOS SENDO EXIBIDOS -->
        <div v-else class="w-full h-full">
          <!-- Exibe o componente de texto estático, caso esteja configurado para isso -->
          <StaticTextDisplay
            v-if="isStaticTextDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :content="summaryContent"
          />
          <!-- Exibe o componente de imagem estático, caso esteja configurado para isso -->
          <StaticImageDisplay
            v-if="isStaticImageDisplay"
            :content="summaryContent"
          />
        </div>
      </div>
    </div>
  </div>
</template>
